"use client";

import { RadixSelect, RadixSelectItem, Typography, cn, useMediaQuery } from "@tosize/ui";
import { useMemo } from "react";
import { StyledLink } from "~/components/Link";
import { SmallBackgroundImage } from "~/components/layout/SmallBackground";
import { useRouter } from "~/hooks/useRouter";

interface NavProps {
	title: string;
	items: { id: string; title: string; href: string | undefined; active?: boolean }[];
}

export default function Navigation({ title, items }: NavProps) {
	const isMobileQuery = useMediaQuery({ max: "sm" });

	const filteredItems = useMemo(() => items?.filter((item) => item.href !== "/"), [items]);
	const hasActiveItem = filteredItems?.some((item) => item.active && item.href !== "/");
	const router = useRouter();
	if (Number(filteredItems?.length) <= 1 || !hasActiveItem) {
		return null;
	}

	return (
		<SmallBackgroundImage className="flex items-end justify-center">
			{filteredItems && (
				<div className="flex w-full justify-center">
					{isMobileQuery ? (
						<div className="mx-4 w-full rounded-t bg-white p-4">
							<RadixSelect
								title={title}
								defaultValue={filteredItems.find((item) => item.active)?.id}
								className="w-full"
								onValueChange={(e) => {
									const current = filteredItems.find((item) => item.id === e);
									if (current?.href) {
										router.push(current.href);
									}
								}}
							>
								{filteredItems.map((item) => (
									<RadixSelectItem value={item.id} key={item.id}>
										{item.title}
									</RadixSelectItem>
								))}
							</RadixSelect>
						</div>
					) : (
						filteredItems.map(({ title, href, active = false }, index) => (
							<NavigationItem title={title} href={href} active={active} key={index} />
						))
					)}
				</div>
			)}
		</SmallBackgroundImage>
	);
}

interface NavigationItemProps {
	title: string;
	href: string | undefined;
	active: boolean;
}

function NavigationItem({ title, href, active }: NavigationItemProps) {
	return (
		<StyledLink
			href={href}
			className={cn("mx-1 hyphens-auto break-all rounded-t px-5 py-2.5 text-center leading-tight", {
				"bg-white focus-visible:underline": active,
				"hover:!no-underline bg-primary hover:bg-primary-medium focus-visible:bg-primary-medium focus-visible:no-underline":
					!active,
			})}
			title={title}
		>
			<Typography
				variant="h6"
				className="inline-block md:flex"
				color={active ? "text-primary-medium" : "text-white"}
			>
				{title}
			</Typography>
		</StyledLink>
	);
}
